import TicketLink from './TicketLink';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { downloadCsv, downloadXlsx } from '../lib/exportTable';
import './ContractorBilled.css';
import { hours, hoursDelta } from '../lib/hours';
import { money } from '../lib/money';
import { ListTools, StatusBadge } from './ListTools';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


// Shared, because three local copies is how one of them came to render
// ten hours as 1h. See lib/hours.js.
const hrs = hours;
const day = (v) => (v ? String(v).slice(0, 10) : '—');

/**
 * The columns, declared once.
 *
 * The table on screen and the two downloads are generated from this list, so
 * a column added here appears in all three and they cannot drift - which is
 * the entire reason anyone trusts an exported figure enough to put it in front
 * of an accountant.
 */
const COLUMNS = [
  { key: 'invoice_number',   label: 'Invoice',      width: 16 },
  { key: 'ticket_number',    label: 'Ticket',       width: 14 },
  { key: 'ticket_title',     label: 'Job',          width: 40 },
  // The ticket's status - Closed once invoiced, or Awaiting Approval again
  // if a reopen was granted. Shown highlighted on screen, and in the file so
  // the two agree.
  { key: 'ticket_status',    label: 'Ticket status', width: 16 },
  { key: 'category',         label: 'Category',     width: 16 },
  { key: 'work_order_number', label: 'Work order',  width: 16 },
  { key: 'requester_name',   label: 'Requested by', width: 18 },
  { key: 'approver_name',    label: 'Approved by',  width: 18 },
  { key: 'approved_at',      label: 'Approved',     width: 12, type: 'date' },
  { key: 'billed_at',        label: 'Billed',       width: 12, type: 'date' },
  { key: 'hours_worked',     label: 'Hours billed', width: 13, type: 'number' },
  { key: 'logged_hours',     label: 'Hours logged', width: 13, type: 'number' },
  { key: 'hours_variance',   label: 'Variance',     width: 11, type: 'number' },
  { key: 'hourly_rate',      label: 'Rate',         width: 10, type: 'money' },
  { key: 'labour_amount',    label: 'Labour',       width: 12, type: 'money' },
  { key: 'expenses_amount',  label: 'Expenses',     width: 12, type: 'money' },
  { key: 'total_amount',     label: 'Amount',       width: 12, type: 'money' },
  { key: 'status',           label: 'Status',       width: 12 }
];

/**
 * Work a contractor has put on an invoice.
 *
 * The Billing tab is a queue - a finished ticket appears, gets an invoice
 * number, and disappears - which is right for working through it and useless
 * a month later, when the question is "what was on invoice 41, and were the
 * hours right". Nothing in the app could answer that. This is the other half.
 *
 * Two hours columns on purpose. One is what was billed, typed into the billing
 * form at the time; the other is the sum of the sessions actually recorded
 * against the ticket. They usually agree, and the cases where they do not are
 * exactly what somebody checking an invoice is looking for.
 */
export default function ContractorBilled({ contractorId, token }) {
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]);

  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoice, setInvoice] = useState('all');
  const [approver, setApprover] = useState('all');
  const [requester, setRequester] = useState('all');
  const [search, setSearch] = useState('');
  // Gordon: Issued Bills had filters and no sort, and no ticket status.
  // 'listed' is the server's own order - invoice by invoice, which is how
  // somebody checking a bill reads it - and stays the default.
  const [sortBy, setSortBy] = useState('listed');
  const [ticketStatus, setTicketStatus] = useState('all');
  const [approvers, setApprovers] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Asking for an invoiced job back.
  //
  // The reopen flow existed and was reachable from the contractor's own ticket
  // page and from the approver's queue, but not from here - which is the one
  // place a contractor is looking when they notice a mistake, because this is
  // the list of what has already been invoiced. So the whole feature was a
  // step away from the screen that prompts it.
  //
  // It raises a REQUEST rather than reopening anything: the hours have been
  // signed off and an invoice has gone out, so an approver agrees first.
  // Granting it is what withdraws the invoice number and puts the other
  // tickets on it back into Ready to Bill.
  const [asking, setAsking] = useState(null);     // ticket_id being asked about
  const [why, setWhy] = useState('');
  const [sending, setSending] = useState(false);
  const [askError, setAskError] = useState(null);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/contractors/${contractorId}/billed`, auth);
      setRows(res.data?.rows || []);
      setInvoices(res.data?.invoices || []);
      setApprovers(res.data?.approvers || []);
      setRequesters(res.data?.requesters || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your billed work');
    } finally {
      setLoading(false);
    }
  }, [contractorId, auth]);

  useEffect(() => { load(); }, [load]);

  const askToReopen = async (ticketId) => {
    if (!why.trim()) return;
    setSending(true);
    setAskError(null);
    try {
      await axios.post(`${API_BASE}/tickets/${ticketId}/reopen-requests`,
        { reason: why.trim() }, auth);
      setAsking(null);
      setWhy('');
      // Reloaded rather than patched in memory, so the row shows the state the
      // server actually holds - including when the request was refused because
      // one was already waiting.
      await load();
    } catch (err) {
      setAskError(err.response?.data?.error || 'Could not send that request');
    } finally {
      setSending(false);
    }
  };

  // Filtered here rather than by refetching: the whole of a contractor's billed
  // work is a few hundred rows at most, and doing it locally keeps typing in
  // the search box instant instead of a request per keystroke.
  const needle = search.trim().toLowerCase();
  const when = (v) => (v ? new Date(v).getTime() : 0);
  const SORTS = {
    billed_desc: (a, b) => when(b.billed_at) - when(a.billed_at),
    billed_asc: (a, b) => when(a.billed_at) - when(b.billed_at),
    invoice: (a, b) => String(a.invoice_number || '').localeCompare(String(b.invoice_number || ''), undefined, { numeric: true })
      || when(b.billed_at) - when(a.billed_at),
    amount: (a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0),
    hours: (a, b) => Number(b.hours_worked || 0) - Number(a.hours_worked || 0),
    // The rows somebody checking an invoice is actually looking for.
    variance: (a, b) => Math.abs(Number(b.hours_variance || 0)) - Math.abs(Number(a.hours_variance || 0)),
    status: (a, b) => String(a.ticket_status || '').localeCompare(String(b.ticket_status || ''))
      || when(b.billed_at) - when(a.billed_at)
  };
  const shown = rows.filter((r) => {
    if (invoice !== 'all' && r.invoice_number !== invoice) return false;
    if (ticketStatus !== 'all' && r.ticket_status !== ticketStatus) return false;
    if (approver !== 'all' && r.approver_id !== approver) return false;
    if (requester !== 'all' && r.requester_id !== requester) return false;
    if (!needle) return true;
    return [r.invoice_number, r.ticket_number, r.ticket_title,
            r.requester_name, r.approver_name, r.work_order_number]
      .some((v) => String(v || '').toLowerCase().includes(needle));
  }).sort(SORTS[sortBy] || (() => 0));
  const filtered = invoice !== 'all' || approver !== 'all' || requester !== 'all'
    || ticketStatus !== 'all' || Boolean(needle);
  const statusesHere = [...new Set(rows.map((r) => r.ticket_status).filter(Boolean))];

  // Recomputed from what is on screen rather than read from the server's
  // totals, so the figures always describe the rows actually shown - including
  // once an invoice filter is applied. The download follows the same rows, so
  // the file and the screen agree by construction rather than by care.
  const sum = shown.reduce((acc, r) => ({
    jobs: acc.jobs + 1,
    billed: acc.billed + Number(r.hours_worked || 0),
    logged: acc.logged + Number(r.logged_hours || 0),
    expenses: acc.expenses + Number(r.expenses_amount || 0),
    labour: acc.labour + Number(r.labour_amount || 0),
    amount: acc.amount + Number(r.total_amount || 0)
  }), { jobs: 0, billed: 0, logged: 0, expenses: 0, labour: 0, amount: 0 });

  const base = invoice !== 'all' ? `invoice-${invoice}`
    : filtered ? 'billed-work-filtered' : 'billed-work';

  if (loading) return <p className="cb-quiet">Loading your billed work&hellip;</p>;

  return (
    <div className="cb">
      {error && <p className="cb-error" role="alert">{error}</p>}

      {rows.length === 0 && !error ? (
        <p className="cb-quiet">
          Nothing has been invoiced yet. A job appears here once you give it an
          invoice number on the <strong>Billing</strong> tab, and stays for good
          &mdash; so this is where to look when somebody asks what a particular
          invoice covered.
        </p>
      ) : (
        <>
          <div className="cb-bar">
            <div className="cb-figures">
              <span><strong>{sum.jobs}</strong> job{sum.jobs === 1 ? '' : 's'}</span>
              <span><strong>{hrs(sum.billed)}</strong> billed</span>
              <span className={Math.abs(sum.logged - sum.billed) > 0.009 ? 'cb-differs' : undefined}>
                <strong>{hrs(sum.logged)}</strong> logged
              </span>
              {/* Only when there is something to reconcile - an "Expenses
                  EUR 0.00" on every invoice is noise, and a total that
                  silently includes them is worse. This is the contractor's
                  own money back, so it is the figure they look for. */}
              {sum.expenses > 0 && (
                <span><strong>{money(sum.expenses)}</strong> reimbursed</span>
              )}
              <span className="cb-amount"><strong>{money(sum.amount)}</strong></span>
            </div>

            <div className="cb-controls">
              {/* One box that looks everywhere, because somebody hunting for a
                  job usually remembers a name or half an invoice number, not
                  which column it lives in. */}
              <label className="cb-filter cb-search">
                <span className="cb-sr">Search</span>
                <input
                  type="search"
                  value={search}
                  placeholder="Invoice, ticket, job or person"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              {invoices.length > 1 && (
                <label className="cb-filter">
                  <span className="cb-sr">Invoice</span>
                  <select value={invoice} onChange={(e) => setInvoice(e.target.value)}>
                    <option value="all">All invoices</option>
                    {invoices.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              )}
              {approvers.length > 1 && (
                <label className="cb-filter">
                  <span className="cb-sr">Approver</span>
                  <select value={approver} onChange={(e) => setApprover(e.target.value)}>
                    <option value="all">All approvers</option>
                    {approvers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </label>
              )}
              {requesters.length > 1 && (
                <label className="cb-filter">
                  <span className="cb-sr">Requester</span>
                  <select value={requester} onChange={(e) => setRequester(e.target.value)}>
                    <option value="all">All requesters</option>
                    {requesters.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </label>
              )}
              {filtered && (
                <button type="button" className="cb-btn"
                        onClick={() => { setInvoice('all'); setApprover('all');
                                         setRequester('all'); setSearch('');
                                         setTicketStatus('all'); }}>
                  Clear
                </button>
              )}
              <button type="button" className="cb-btn"
                      onClick={() => downloadCsv(COLUMNS, shown, base)}>
                Download CSV
              </button>
              <button type="button" className="cb-btn"
                      onClick={() => downloadXlsx(COLUMNS, shown, base, 'Billed')}>
                Download Excel
              </button>
            </div>
          </div>

          {/* Sort and the ticket-status filter, on the row under the figures.
              The search and the invoice, approver and requester filters above
              were here already; this is the half that was missing. */}
          <ListTools
            sortBy={sortBy} onSort={setSortBy}
            sorts={[
              { key: 'listed', label: 'As invoiced' },
              { key: 'billed_desc', label: 'Most recently billed' },
              { key: 'billed_asc', label: 'Earliest billed' },
              { key: 'invoice', label: 'Invoice number' },
              { key: 'amount', label: 'Largest amount' },
              { key: 'hours', label: 'Most hours' },
              { key: 'variance', label: 'Biggest hours variance' },
              { key: 'status', label: 'Ticket status' }
            ]}
            status={ticketStatus} onStatus={setTicketStatus} statuses={statusesHere}
          />

          {Math.abs(sum.logged - sum.billed) > 0.009 && (
            <p className="cb-note">
              The hours logged against these jobs differ from the hours billed by{' '}
              {hrs(Math.abs(sum.logged - sum.billed))}. That is not necessarily wrong
              &mdash; time recorded after an invoice went out will do it &mdash; but it is
              worth a look before the next one.
            </p>
          )}

          <div className="cb-scroll">
            <table className="cb-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} scope="col"
                        className={c.type === 'number' || c.type === 'money' ? 'cb-num' : undefined}>
                      {c.label}
                    </th>
                  ))}
                  {/* Outside COLUMNS on purpose. That list drives the table AND
                      both downloads, so anything added to it appears in the
                      exported file - and a button is not a figure an
                      accountant should find in a spreadsheet. */}
                  <th scope="col">Reopen</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="cb-invoice">{r.invoice_number}</td>
                    <td className="cb-ticket">
                      <TicketLink id={r.ticket_id}>{r.ticket_number || '—'}</TicketLink>
                    </td>
                    <td>{r.ticket_title || '—'}</td>
                    <td><StatusBadge status={r.ticket_status} /></td>
                    <td>{r.category || '—'}</td>
                    <td className="cb-ticket">{r.work_order_number || '—'}</td>
                    <td>{r.requester_name || '—'}</td>
                    <td>{r.approver_name || <span className="cb-quiet-inline">not recorded</span>}</td>
                    <td>{day(r.approved_at)}</td>
                    <td>{day(r.billed_at)}</td>
                    <td className="cb-num">{hrs(r.hours_worked)}</td>
                    <td className="cb-num">{hrs(r.logged_hours)}</td>
                    <td className={`cb-num${Math.abs(r.hours_variance) > 0.009 ? ' cb-differs' : ''}`}>
                      {hoursDelta(r.hours_variance)}
                    </td>
                    <td className="cb-num">{money(r.hourly_rate)}</td>
                    {/* total_amount is hours * rate PLUS what was reimbursed,
                        and expenses_amount had been written since migration
                        021 and read by nothing - so this row showed a rate,
                        a number of hours, and a total that did not follow
                        from them the moment a job carried a claim. */}
                    <td className="cb-num">{money(r.labour_amount)}</td>
                    <td className={`cb-num${r.expenses_amount > 0 ? ' cb-differs' : ''}`}>
                      {r.expenses_amount > 0 ? money(r.expenses_amount) : '—'}
                    </td>
                    <td className="cb-num">{money(r.total_amount)}</td>
                    <td>
                      <span className={`cb-status cb-${r.status || 'invoiced'}`}>
                        {r.status || 'invoiced'}
                      </span>
                    </td>
                    <td className="cb-reopen">
                      {r.reopen_pending ? (
                        // Said plainly, because a contractor who cannot see
                        // that they already asked will press the button again
                        // and meet a refusal that reads like a broken button.
                        <span className="cb-quiet-inline">With an approver</span>
                      ) : asking === r.ticket_id ? (
                        <div className="cb-ask">
                          <label htmlFor={`cb-why-${r.id}`} className="cb-ask-label">
                            What needs changing?
                          </label>
                          <textarea
                            id={`cb-why-${r.id}`}
                            className="cb-ask-why"
                            value={why}
                            autoFocus
                            onChange={(e) => setWhy(e.target.value)}
                            placeholder="e.g. I logged 3 hours but it was 5 — the second visit never went on."
                          />
                          <div className="cb-ask-buttons">
                            <button
                              type="button"
                              className="cb-ask-send"
                              disabled={sending || !why.trim()}
                              onClick={() => askToReopen(r.ticket_id)}
                            >
                              Send to the approver
                            </button>
                            <button
                              type="button"
                              className="cb-ask-cancel"
                              onClick={() => { setAsking(null); setWhy(''); setAskError(null); }}
                            >
                              Cancel
                            </button>
                          </div>
                          {askError && <p className="cb-ask-error">{askError}</p>}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="cb-ask-open"
                          onClick={() => { setAsking(r.ticket_id); setWhy(''); setAskError(null); }}
                        >
                          Ask to reopen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={10}>{sum.jobs} job{sum.jobs === 1 ? '' : 's'}</td>
                  <td className="cb-num">{hrs(sum.billed)}</td>
                  <td className="cb-num">{hrs(sum.logged)}</td>
                  <td />
                  <td />
                  <td className="cb-num">{money(sum.labour)}</td>
                  <td className="cb-num">
                    {sum.expenses > 0 ? money(sum.expenses) : '—'}
                  </td>
                  <td className="cb-num">{money(sum.amount)}</td>
                  <td />
                  {/* The Reopen column. Without this the footer is one cell
                      short and every total shifts left of its heading. */}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
